function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    console.log('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  console.log('onKeyUpEscape');
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class wishListButton extends HTMLElement {
  constructor() {
    super();
    this.storageKey = 'WishList';
    this.addEventListener('click', this.handleClick.bind(this));
    this.initializeButton();
  }

  getWishlistFromStorage() {
    const storedData = localStorage.getItem(this.storageKey);
    return storedData ? JSON.parse(storedData) : [];
  }

  saveWishlistToStorage(wishlistArray) {
    localStorage.setItem(this.storageKey, JSON.stringify(wishlistArray));
    document.querySelectorAll('wish-list-count').forEach(el => el.updateCount()); // Update count
  }

  isItemInWishlist(wishlistArray, itemId) {
    return wishlistArray.some(item => item.id === itemId);
  }

  handleClick(event) {
    event.preventDefault();
    const itemId = this.getAttribute('data-id');
    const itemUrl = this.getAttribute('data-url');
    const wishlistArray = this.getWishlistFromStorage();

    if (this.isItemInWishlist(wishlistArray, itemId)) {
      const updatedWishlist = wishlistArray.filter(item => item.id !== itemId);
      this.saveWishlistToStorage(updatedWishlist);
      this.classList.remove('active');
    } else {
      wishlistArray.push({ id: itemId, url: itemUrl });
      this.saveWishlistToStorage(wishlistArray);
      this.classList.add('active');
    }
  }

  initializeButton() {
    const itemId = this.getAttribute('data-id');
    const wishlistArray = this.getWishlistFromStorage();

    if (this.isItemInWishlist(wishlistArray, itemId)) {
      this.classList.add('active');
    } else {
      this.classList.remove('active');
    }
  }
}
customElements.define('wish-list-button', wishListButton);

class wishListItems extends HTMLElement {
  constructor() {
    super();
    this.storageKey = 'WishList';
    this.processWishlistItems();
  }

  getWishlistFromStorage() {
    const storedData = localStorage.getItem(this.storageKey);
    return storedData ? JSON.parse(storedData) : [];
  }

  async processWishlistItems() {
    const wishlist = this.getWishlistFromStorage();
    const container = this.querySelector('#WishListItems');
    if (!container) return;

    for (const item of wishlist) {
      try {
        const response = await fetch(item.url);
        if (!response.ok) throw new Error(`Request failed with status ${response.status}`);

        const htmlText = await response.text();
        const doc = new DOMParser().parseFromString(htmlText, 'text/html');
        const gridItem = doc.querySelector('.grid__item');

        if (gridItem) container.appendChild(gridItem);
      } catch (error) {
        console.error(`Error processing item with ID ${item.id}:`, error);
      }
    }
  }
}
customElements.define('wish-list-items', wishListItems);

class wishListCount extends HTMLElement {
  constructor() {
    super();
    this.storageKey = 'WishList';
    this.updateCount();
  }

  getWishlistFromStorage() {
    const storedData = localStorage.getItem(this.storageKey);
    return storedData ? JSON.parse(storedData) : [];
  }

  updateCount() {
    const wishlistArray = this.getWishlistFromStorage();
    this.textContent = wishlistArray.length;
  }
}
customElements.define('wish-list-count', wishListCount);



class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    // Force animations - ignore user motion preferences

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        false
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);
class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add('overflow-hidden'); // Apply scroll lock
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
    document.body.classList.remove('overflow-hidden'); // Remove scroll lock
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}


customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);








class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPage = 0;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.dotsContainer = this.querySelector('.slider-dots');
    this.isVertical = this.classList.contains('slider-vertical');

    if (!this.slider || !this.nextButton) return;

    this.itemsPerScroll = parseInt(this.slider.getAttribute('data-items-per-scroll')) || 1;
    this.initPages();

    const resizeObserver = new ResizeObserver(() => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.debounce(this.update.bind(this), 100));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));

    if (this.slider.getAttribute('data-autoplay') === 'true') {
      this.setAutoPlay();
    }
  }

  debounce(func, wait) {
    let timeout;
    return function (...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientHeight > 0);
    if (this.sliderItemsToShow.length < 2) return;

    this.sliderItemOffset = this.isVertical
      ? this.sliderItemsToShow[1].offsetTop - this.sliderItemsToShow[0].offsetTop
      : this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;

    this.slidesPerPage = Math.floor(
      (this.isVertical ? this.slider.clientHeight : this.slider.clientWidth) /
      this.sliderItemOffset
    );

    this.totalPages = Math.ceil(this.sliderItemsToShow.length / this.itemsPerScroll);

    if (this.dotsContainer) {
      this.initDots();
    }
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  initDots() {
    this.dotsContainer.innerHTML = '';
    for (let i = 0; i < this.totalPages; i++) {
      const dot = document.createElement('button');
      dot.classList.add('slider-dot');
      if (i === 0) dot.classList.add('active');
      dot.addEventListener('click', () => this.goToPage(i));
      this.dotsContainer.appendChild(dot);
    }
    this.dots = this.dotsContainer.querySelectorAll('.slider-dot');
  }

  update() {
    if (!this.slider || !this.nextButton) return;

    // Calculate current page based on scroll position
    const scrollPosition = this.isVertical ? this.slider.scrollTop : this.slider.scrollLeft;
    const newPage = Math.min(
      Math.max(0, Math.round(scrollPosition / (this.itemsPerScroll * this.sliderItemOffset))),
      this.totalPages - 1
    );

    if (newPage !== this.currentPage) {
      this.currentPage = newPage;
      this.updateDots();

      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage * this.itemsPerScroll],
          },
        })
      );
    }

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage + 1;
      this.pageTotalElement.textContent = this.totalPages;
    }

    this.toggleButtonStates();
  }

  updateDots() {
    if (!this.dots || this.dots.length === 0) return;
    this.dots.forEach((dot, index) => {
      dot.classList.toggle('active', index === this.currentPage);
    });
  }

  goToPage(page) {
    const position = Math.min(
      page * this.itemsPerScroll * this.sliderItemOffset,
      (this.totalPages - 1) * this.itemsPerScroll * this.sliderItemOffset
    );
    this.setSlidePosition(position);
  }

  toggleButtonStates() {
    if (this.currentPage === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.currentPage >= this.totalPages - 1) {
      this.nextButton.removeAttribute('disabled'); // Keep enabled to allow reset
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide =
      (this.isVertical ? this.slider.clientHeight : this.slider.clientWidth) +
      (this.isVertical ? this.slider.scrollTop : this.slider.scrollLeft) -
      offset;

    return this.isVertical
      ? element.offsetTop + element.clientHeight <= lastVisibleSlide &&
          element.offsetTop >= this.slider.scrollTop
      : element.offsetLeft + element.clientWidth <= lastVisibleSlide &&
          element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = this.itemsPerScroll;

    if (event.currentTarget.name === 'next') {
      let targetPage = this.currentPage + 1;
      if (targetPage >= this.totalPages) {
        this.setSlidePosition(0); // Reset to start
        this.currentPage = 0;
      } else {
        const targetPosition = targetPage * step * this.sliderItemOffset;
        this.setSlidePosition(targetPosition);
        this.currentPage = targetPage;
      }
    } else { // Previous
      let targetPage = this.currentPage - 1;
      if (targetPage < 0) {
        const lastPagePosition = (this.totalPages - 1) * step * this.sliderItemOffset;
        this.setSlidePosition(lastPagePosition);
        this.currentPage = this.totalPages - 1;
      } else {
        const targetPosition = targetPage * step * this.sliderItemOffset;
        this.setSlidePosition(targetPosition);
        this.currentPage = targetPage;
      }
    }
    this.update(); // Ensure UI updates immediately
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      [this.isVertical ? 'top' : 'left']: position,
      behavior: 'smooth',
    });
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000 || 3000;
    this.play();
    this.addEventListener('mouseover', this.pause.bind(this));
    this.addEventListener('mouseleave', this.play.bind(this));
  }

  play() {
    this.pause();
    this.autoplay = setInterval(() => {
      let targetPage = this.currentPage + 1;
      if (targetPage >= this.totalPages) {
        this.setSlidePosition(0);
        this.currentPage = 0;
      } else {
        const targetPosition = targetPage * this.itemsPerScroll * this.sliderItemOffset;
        this.setSlidePosition(targetPosition);
        this.currentPage = targetPage;
      }
      this.update();
    }, this.autoplaySpeed);
  }

  pause() {
    clearInterval(this.autoplay);
  }
}

customElements.define('slider-component', SliderComponent);








class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      // Force animations - ignore user motion preferences
      // Always enable autoplay regardless of motion preferences

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}
function formatPrice(price) {
    let formattedPrice;

    // Check if the format uses a dot or comma for decimals
    if (window.storeMoneyFormat.includes("0.00")) {
        formattedPrice = window.storeMoneyFormat.replace("0.00", (price/100).toFixed(2));
    } else if (window.storeMoneyFormat.includes("0,00")) {
        // Replace dot with comma if using comma format
        formattedPrice = window.storeMoneyFormat.replace("0,00", (price/100).toFixed(2).replace(".", ","));
    } else {
        // If no specific format is found, fall back to default "$" + price format
        formattedPrice = "€" + price.toFixed(2);
    }

    return formattedPrice;
}

// Slide Up function
function slideUp(element, duration = 300) {
  // Check if the element is already collapsed (height = 0 or display is none)
  if (element.offsetHeight === 0 || window.getComputedStyle(element).display === 'none') {
    return; // No animation needed if already collapsed or hidden
  }

  element.style.height = `${element.offsetHeight}px`; // Set the current height
  element.style.transitionProperty = 'height, margin, padding';
  element.style.transitionDuration = `${duration}ms`;
  element.style.overflow = 'hidden';

  requestAnimationFrame(() => {
    element.style.height = '0'; // Collapse the element's height
    element.style.paddingTop = '0';
    element.style.paddingBottom = '0';
    element.style.marginTop = '0';
    element.style.marginBottom = '0';
  });

  setTimeout(() => {
    element.style.display = 'none'; // Hide element after transition
    element.style.removeProperty('height');
    element.style.removeProperty('padding-top');
    element.style.removeProperty('padding-bottom');
    element.style.removeProperty('margin-top');
    element.style.removeProperty('margin-bottom');
    element.style.removeProperty('overflow');
    element.style.removeProperty('transition-duration');
    element.style.removeProperty('transition-property');
  }, duration);
}

function slideDown(element, duration = 300) {
  // Check if the element is already expanded (height > 0 and visible)
  const isVisible = element.offsetHeight > 0 && window.getComputedStyle(element).display !== 'none';
  if (isVisible) {
    console.log('element is visible');
    return; // No animation needed if already expanded and visible
  }
    console.log('element is not visible');

  // Remove the display property if it's set to none
  element.style.removeProperty('display');
  let display = window.getComputedStyle(element).display;
  if (display === 'none') display = 'block'; // Make it visible if it was hidden
  element.style.display = display;

  // Get the full content height
  let height = element.scrollHeight;
  element.style.overflow = 'hidden';
  element.style.height = '0'; // Start with collapsed height
  element.style.paddingTop = '0';
  element.style.paddingBottom = '0';
  element.style.marginTop = '0';
  element.style.marginBottom = '0';
  element.offsetHeight; // Force repaint to apply the initial styles

  // Apply the transition styles
  element.style.transitionProperty = 'height, margin, padding';
  element.style.transitionDuration = `${duration}ms`;
  element.style.height = `${height}px`; // Expand to content height
  element.style.removeProperty('padding-top');
  element.style.removeProperty('padding-bottom');
  element.style.removeProperty('margin-top');
  element.style.removeProperty('margin-bottom');

  // Remove transition styles after the duration
  setTimeout(() => {
    element.style.removeProperty('height');
    element.style.removeProperty('overflow');
    element.style.removeProperty('transition-duration');
    element.style.removeProperty('transition-property');
  }, duration);
}

function updateWindowHeight() {
  const windowHeight = window.innerHeight;
  document.documentElement.style.setProperty('--window-height', `${windowHeight}px`);
  console.log(`Window height updated: ${windowHeight}px`);

  
      const announcementBar = document.querySelector('.announcement-bar-section');
  
  if (announcementBar) {
    console.log('found announcementBar');
    const announcementHeight = announcementBar.getBoundingClientRect().height; // Get the height of the element
    document.documentElement.style.setProperty('--announcement-height', `${announcementHeight}px`);
  }
    else{
    console.log('not found announcementBar');
    }

  
}

// Update the height initially
updateWindowHeight();

// Update the height on window resize
window.addEventListener('resize', updateWindowHeight);


// ⭐ OPTIMIZED TRUSTPILOT RATING SYSTEM ⭐
// Single, efficient implementation that was working before

document.addEventListener("DOMContentLoaded", function () {
    // Configuration constants
    const CONFIG = {
        CACHE_KEY: 'trustpilot_rating_data',
        CACHE_DURATION: 24 * 60 * 60 * 1000, // 24 hours
        WIDGET_TIMEOUT: 8000, // Reduced from 10s to 8s for faster fallback
        UPDATE_DELAY: 500, // Reduced from 2000ms for faster updates
        SELECTORS: {
            rating: '.es-rating-value',
            reviews: '.es-badge-total-reviews'
        }
    };

    // Cache management - fast and efficient
    const Cache = {
        set(rating, reviews) {
            try {
                localStorage.setItem(CONFIG.CACHE_KEY, JSON.stringify({
                    rating, reviews, timestamp: Date.now()
                }));
            } catch (e) {
                console.warn('[Trustpilot] Cache storage failed:', e);
            }
        },
        
        get() {
            try {
                const data = localStorage.getItem(CONFIG.CACHE_KEY);
                if (!data) return null;
                
                const parsed = JSON.parse(data);
                const isExpired = Date.now() - parsed.timestamp > CONFIG.CACHE_DURATION;
                
                return isExpired ? null : parsed;
            } catch (e) {
                console.warn('[Trustpilot] Cache retrieval failed:', e);
                return null;
            }
        }
    };

    // Optimized display updater - only touches necessary elements
    function updateDisplays(rating, reviews) {
        const formattedRating = rating.toString().replace('.', ',');
        
        // Batch DOM updates for performance
        requestAnimationFrame(() => {
            // Update rating scores
            document.querySelectorAll('.rating-score, .product-rating-score').forEach(el => {
                el.textContent = formattedRating;
            });
            
            // Update footer rating with special format
            document.querySelectorAll('.footer-rating-score').forEach(el => {
                el.textContent = `${formattedRating} / 5.0`;
            });
            
            // Update review counts
            document.querySelectorAll('.product-rating-count, .footer-rating-count').forEach(el => {
                el.textContent = reviews;
            });
            
            // Update header rating count with special format
            document.querySelectorAll('.rating-count').forEach(el => {
                if (el.textContent.includes('avaliações')) {
                    el.textContent = `(${reviews} avaliações)`;
                    el.classList.add('loaded');
                }
            });
            
            // Update star displays efficiently
            const starContainers = document.querySelectorAll('.product-stars-container, .footer-stars-container');
            starContainers.forEach(container => updateProductStars(container, parseFloat(rating)));
        });
    }

    // Fast widget data extraction
    function extractWidgetData() {
        const ratingEl = document.querySelector(CONFIG.SELECTORS.rating);
        const reviewsEl = document.querySelector(CONFIG.SELECTORS.reviews);
        
        if (!ratingEl || !reviewsEl) return null;
        
        const rating = ratingEl.textContent.trim();
        const reviews = reviewsEl.textContent.replace(/\D/g, '');
        
        return (rating && rating !== 'N/A' && reviews && reviews !== '0') 
            ? { rating, reviews } 
            : null;
    }

    // Efficient widget observer - only observes when needed
    function setupWidgetObserver() {
        let timeoutId = setTimeout(() => {
            console.warn('[Trustpilot] Widget timeout - using cached data');
            const cached = Cache.get();
            if (cached) updateDisplays(cached.rating, cached.reviews);
        }, CONFIG.WIDGET_TIMEOUT);

        const observer = new MutationObserver(() => {
            const data = extractWidgetData();
            if (data) {
                clearTimeout(timeoutId);
                observer.disconnect();
                
                Cache.set(data.rating, data.reviews);
                updateDisplays(data.rating, data.reviews);
                console.log(`[Trustpilot] Live data: ${data.rating} (${data.reviews} reviews)`);
            }
        });

        observer.observe(document.body, { 
            childList: true, 
            subtree: true 
        });
        
        return () => {
            clearTimeout(timeoutId);
            observer.disconnect();
        };
    }

    // Main initialization - fast and efficient
    function initialize() {
        // 0. Add CSS for smooth rating count transitions
        if (!document.getElementById('trustpilot-styles')) {
            const style = document.createElement('style');
            style.id = 'trustpilot-styles';
            style.textContent = `
                .rating-count {
                    opacity: 0;
                    transition: opacity 0.3s ease;
                }
                .rating-count.loaded {
                    opacity: 1;
                }
            `;
            document.head.appendChild(style);
        }
        
        // 1. Apply cached data immediately (prevents flicker)
        const cached = Cache.get();
        if (cached) {
            updateDisplays(cached.rating, cached.reviews);
            console.log(`[Trustpilot] Cached data applied: ${cached.rating} (${cached.reviews} reviews)`);
        }
        
        // 2. Try to get fresh data from widget
        setTimeout(() => {
            const fresh = extractWidgetData();
            if (fresh) {
                Cache.set(fresh.rating, fresh.reviews);
                updateDisplays(fresh.rating, fresh.reviews);
                console.log(`[Trustpilot] Fresh data: ${fresh.rating} (${fresh.reviews} reviews)`);
            } else {
                // 3. Setup observer for widget loading
                setupWidgetObserver();
            }
        }, CONFIG.UPDATE_DELAY);
        
        // 4. Ensure widgets are properly hidden but functional
        ['trust-widget', 'product-trust-widget'].forEach(id => {
            const widget = document.getElementById(id);
            if (widget) {
                Object.assign(widget.style, {
                    position: 'absolute',
                    visibility: 'hidden',
                    opacity: '0',
                    pointerEvents: 'none',
                    display: 'block' // Ensures widget loads
                });
            }
        });
    }

    // Start the optimized system
    initialize();
});



/** New Search Bar Desktop **/

document.addEventListener("DOMContentLoaded", function () {
    const searchInput = document.querySelector(".header-search-input");
    const searchContainer = document.querySelector(".header-search-container");
    const resultsContainer = document.querySelector(".search-results");

    let timeoutId; // To prevent flickering on fast typing

    searchInput.addEventListener("input", function () {
        clearTimeout(timeoutId); // Clear previous timeout to prevent flicker

        const query = searchInput.value.trim();
        if (query.length < 2) {
            resultsContainer.classList.remove("show");
            return;
        }

        timeoutId = setTimeout(() => {
            fetch(`/search/suggest.json?q=${query}&resources[type]=product`)
                .then(response => response.json())
                .then(data => {
                    resultsContainer.innerHTML = "";
                    if (data.resources.results.products.length > 0) {
                        data.resources.results.products.forEach((product, index) => {
                            const item = document.createElement("div");
                            item.classList.add("search-result-item");
                            item.style.animationDelay = `${index * 0.05}s`; // Staggered delay
                            item.innerHTML = `
                                <a href="${product.url}" class="search-result-link">
                                    <img src="${product.image}" alt="${product.title}" class="search-result-image">
                                    <span class="search-result-title">${product.title}</span>
                                </a>
                            `;
                            resultsContainer.appendChild(item);
                            requestAnimationFrame(() => {
                                item.classList.add("fade-in"); // Apply animation
                            });
                        });
                        resultsContainer.classList.add("show");
                    } else {
                        resultsContainer.innerHTML = `<div class="search-result-item">No results found</div>`;
                        resultsContainer.classList.add("show");
                    }
                })
                .catch(error => console.error("Error fetching search results:", error));
        }, 150); // Small delay to allow typing smoothly
    });

    document.addEventListener("click", function (e) {
        if (!searchContainer.contains(e.target)) {
            resultsContainer.classList.remove("show");
        }
    });
});

/* Back in stock */

document.addEventListener("DOMContentLoaded", function () {
    // Select all variant option inputs (radio buttons, dropdowns, etc.)
    const variantInputs = document.querySelectorAll('input[name^="option"], select[name^="option"]');

    variantInputs.forEach(input => {
        input.addEventListener("change", function () {
            const productForm = document.querySelector("product-form");

            if (productForm) {
                // Reload the product form
                productForm.innerHTML = productForm.innerHTML;
            }
        });
    });

    // Prevent clicking on the readonly input
    const quantityInput = document.querySelector('.quantity__input');
    if (quantityInput) {
        quantityInput.addEventListener('click', function(e) {
            if (this.readOnly) {
                e.preventDefault();
                return false;
            }
        });

        // Also prevent focus on the readonly input
        quantityInput.addEventListener('focus', function(e) {
            if (this.readOnly) {
                this.blur();
            }
        });
    }
});











document.addEventListener('DOMContentLoaded', function() {
  const quantityWrapper = document.querySelector('.quantity-wrapper');
  if (!quantityWrapper) return;

  const quantityContainer = quantityWrapper.querySelector('.quantity');
  const quantityInput = quantityContainer.querySelector('.quantity__input');
  const minusButton = quantityContainer.querySelector('button[name="minus"]');
  const plusButton = quantityContainer.querySelector('button[name="plus"]');

function updateQuantityState() {
  const value = parseInt(quantityInput.value, 10);
  const min = parseInt(quantityInput.getAttribute('min')) || 1;
  const max = parseInt(quantityInput.getAttribute('max')) || Infinity;
  const isUnavailable = quantityContainer.hasAttribute('data-unavailable');

  if (isUnavailable || !currentVariantAvailable()) {
    // Product is unavailable or out of stock, disable everything
    quantityContainer.classList.add('quantity--unavailable');
    quantityInput.disabled = true;
    minusButton.disabled = true;
    plusButton.disabled = true;
  } else {
    // Product is available, selectively enable/disable buttons
    quantityContainer.classList.remove('quantity--unavailable');
    quantityInput.disabled = false;
    minusButton.disabled = value <= min; // Disable minus only at min
    plusButton.disabled = value >= max;  // Disable plus only at max
    
    // Make sure minus button is never disabled when at max quantity
    if (value >= max) {
      minusButton.disabled = false;
    }
  }
}

  // Initial state
  updateQuantityState();

  // Update on input change
  quantityInput.addEventListener('input', function() {
    let value = parseInt(quantityInput.value, 10);
    const min = parseInt(quantityInput.getAttribute('min')) || 1;
    const max = parseInt(quantityInput.getAttribute('max')) || Infinity;

    if (value < min) quantityInput.value = min;
    if (max !== Infinity && value > max) quantityInput.value = max;
    updateQuantityState();
  });

  // Plus button click
  plusButton.addEventListener('click', function() {
    const step = parseInt(quantityInput.getAttribute('step')) || 1;
    const max = parseInt(quantityInput.getAttribute('max')) || Infinity;
    let value = parseInt(quantityInput.value, 10);

    if (value < max) {
      quantityInput.value = value + step;
      updateQuantityState();
    }
  });

  // Minus button click
  minusButton.addEventListener('click', function() {
    const step = parseInt(quantityInput.getAttribute('step')) || 1;
    const min = parseInt(quantityInput.getAttribute('min')) || 1;
    let value = parseInt(quantityInput.value, 10);

    if (value > min) {
      quantityInput.value = value - step;
      updateQuantityState();
    }
  });
});



document.addEventListener('DOMContentLoaded', function() {
  // Get reference to the iframe
  const iframe = document.getElementById('gw-stock-fulfilled-iframe');
  
  if (iframe) {
    // Create an overlay div that sits on top of the iframe
    const overlay = document.createElement('div');
    overlay.style.position = 'fixed';
    overlay.style.top = '0';
    overlay.style.left = '0';
    overlay.style.width = '100%';
    overlay.style.height = '100%';
    overlay.style.backgroundColor = 'transparent';
    overlay.style.zIndex = '9998'; // High z-index but below modal
    
    // Function to add the overlay
    function addOverlay() {
      document.body.appendChild(overlay);
      
      // Add click handler to the overlay
      overlay.addEventListener('click', function(e) {
        // Prevent clicks from reaching elements underneath
        e.preventDefault();
        e.stopPropagation();
        
        // Check if click is outside the modal area (approximation)
        const rect = iframe.getBoundingClientRect();
        const centerX = rect.left + rect.width/2;
        const centerY = rect.top + rect.height/2;
        
        // Calculate distance from center of iframe
        const distance = Math.sqrt(
          Math.pow(e.clientX - centerX, 2) + 
          Math.pow(e.clientY - centerY, 2)
        );
        
        // If click is outside central modal area (approximation)
        if (distance > 150) { // Adjust this value based on modal size
          // Close the popup by accessing parent function
          if (window.initVar && window.initVar.closePopUp) {
            window.initVar.closePopUp('gw-stock-fulfilled-iframe');
          }
          
          // Remove the overlay
          document.body.removeChild(overlay);
        }
      });
    }
    
    // Add the overlay when the iframe appears (mutation observer)
    const observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          if (iframe.style.display === 'block') {
            // Wait a bit for the iframe to fully render
            setTimeout(addOverlay, 300);
          }
        }
      });
    });
    
    observer.observe(iframe, { attributes: true });
    
    // If iframe is already visible, add overlay immediately
    if (iframe.style.display === 'block') {
      setTimeout(addOverlay, 300);
    }
  }
});







// Klrana



// Function to update product page stars (SVG-based)
function updateProductStars(container, rating) {
    console.log('[Stars Debug] Updating product stars:', { container, rating });
    // Calculate full and partial stars
    const fullStars = Math.floor(rating);
    
    // Change the condition for half star to ensure 4.7 definitely shows a half star
    const hasHalfStar = (rating - fullStars) >= 0.3;
    
    // Get all star icons in the container
    const stars = container.querySelectorAll('.product-star-icon, .footer-star-icon');
    console.log('[Stars Debug] Found stars:', stars.length);
    
    if (stars.length === 0) {
        console.warn('[Stars Debug] No stars found in container');
        return;
    }
    
    // First, set all stars to empty (gray)
    stars.forEach(star => {
        const path = star.querySelector('svg path');
        if (path) path.setAttribute('fill', '#E0E0E0');
    });
    
    // Then add golden stars based on rating
    stars.forEach((star, index) => {
        const path = star.querySelector('svg path');
        
        if (index < fullStars) {
            // Full star (gold)
            if (path) path.setAttribute('fill', '#FCA900');
        } else if (index === fullStars && hasHalfStar) {
            // Half star - simpler approach to create half gold, half gray star
            const svg = star.querySelector('svg');
            if (svg) {
                // Simplest approach - use CSS class for half-star effect
                // This leverages the CSS styles already defined in the stylesheet
                star.classList.add('half-filled');
                
                // Make sure the star's path is gold initially
                const path = svg.querySelector('path');
                if (path) {
                    path.setAttribute('fill', '#FCA900');
                }
            }
        }
        // For stars beyond the rating (index > fullStars), we already set them gray
    });
}

// Update star icons based on rating value (for text-based stars)
function updateStarDisplay(container, rating, starClass = 'star-icon') {
    console.log('[Stars Debug] Updating star display:', { container, rating, starClass });
    // Clear existing stars
    container.innerHTML = '';
    
    // Calculate full and partial stars
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating - fullStars >= 0.3;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
    
    // Create SVG star template for consistent display
    const createSVGStar = (fill) => {
        console.log('[Stars Debug] Creating SVG star with fill:', fill);
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="15" height="14" viewBox="0 0 15 14" fill="none">
            <path d="M6.53834 0.859966C6.70914 0.449319 7.29086 0.449318 7.46166 0.859964L8.99874 4.55556C9.07075 4.72868 9.23355 4.84696 9.42045 4.86194L13.4102 5.1818C13.8535 5.21734 14.0332 5.77059 13.6955 6.05993L10.6557 8.66378C10.5133 8.78576 10.4512 8.97715 10.4947 9.15952L11.4234 13.0528C11.5265 13.4854 11.0559 13.8273 10.6764 13.5955L7.26063 11.5092C7.10062 11.4115 6.89938 11.4115 6.73937 11.5092L3.32363 13.5955C2.94408 13.8273 2.47345 13.4854 2.57665 13.0528L3.50534 9.15952C3.54881 8.97715 3.48669 8.78576 3.34431 8.66378L0.304464 6.05993C-0.0332407 5.77059 0.146505 5.21734 0.589848 5.1818L4.57955 4.86194C4.76645 4.84696 4.92925 4.72868 5.00126 4.55556L6.53834 0.859966Z" fill="${fill}"/>
        </svg>`;
        console.log('[Stars Debug] Created SVG:', svg);
        return svg;
    };
    
    // Add full stars
    for (let i = 0; i < fullStars; i++) {
        const star = document.createElement('div');
        star.className = `${starClass} filled`;
        star.innerHTML = createSVGStar('#FCA900');
        container.appendChild(star);
    }
    
    // Add half star if needed
    if (hasHalfStar) {
        const star = document.createElement('div');
        star.className = `${starClass} half-filled`;
        star.innerHTML = createSVGStar('#FCA900');
        container.appendChild(star);
        
        // We'll handle half-filled styling with CSS in the container
        // This makes it consistent with the product page implementation
    }
    
    // Add empty stars
    for (let i = 0; i < emptyStars; i++) {
        const star = document.createElement('div');
        star.className = `${starClass} empty`;
        star.innerHTML = createSVGStar('#E0E0E0');
        container.appendChild(star);
    }
}

// Duplicate Trustpilot implementation removed - using optimized version above

// MISSING FUNCTION: Add currentVariantAvailable function for proper availability checking
function currentVariantAvailable() {
  try {
    // Get the current variant from the product info element
    const productInfo = document.querySelector('product-info');
    if (!productInfo) {
      console.warn('Product info element not found');
      return false;
    }
    
    // Try to get the selected variant data
    const variantScript = productInfo.querySelector('[data-selected-variant]');
    if (!variantScript) {
      console.warn('Selected variant script not found');
      return false;
    }
    
    const selectedVariant = JSON.parse(variantScript.textContent);
    if (!selectedVariant) {
      console.warn('No selected variant found');
      return false;
    }
    
    // Use Shopify's built-in availability logic as primary check
    if (!selectedVariant.available) {
      return false;
    }
    
    // Additional inventory checks for more accurate availability
    if (selectedVariant.inventory_management === 'shopify') {
      // If inventory is tracked by Shopify
      if (selectedVariant.inventory_policy === 'continue') {
        // Backorder allowed - always available when variant.available is true
        return true;
      } else {
        // No backorder - check actual inventory
        return selectedVariant.inventory_quantity > 0;
      }
    } else {
      // No inventory tracking - rely on Shopify's availability
      return true;
    }
  } catch (error) {
    console.error('Error checking variant availability:', error);
    return false; // Fail safe - assume unavailable if error
  }
}

document.addEventListener('DOMContentLoaded', function() {
  const quantityWrapper = document.querySelector('.quantity-wrapper');
  if (!quantityWrapper) return;

  const quantityContainer = quantityWrapper.querySelector('.quantity');
  const quantityInput = quantityContainer.querySelector('.quantity__input');
  const minusButton = quantityContainer.querySelector('button[name="minus"]');
  const plusButton = quantityContainer.querySelector('button[name="plus"]');

function updateQuantityState() {
  const value = parseInt(quantityInput.value, 10);
  const min = parseInt(quantityInput.getAttribute('min')) || 1;
  const max = parseInt(quantityInput.getAttribute('max')) || Infinity;
  const isUnavailable = quantityContainer.hasAttribute('data-unavailable');

  if (isUnavailable || !currentVariantAvailable()) {
    // Product is unavailable or out of stock, disable everything
    quantityContainer.classList.add('quantity--unavailable');
    quantityInput.disabled = true;
    minusButton.disabled = true;
    plusButton.disabled = true;
  } else {
    // Product is available, selectively enable/disable buttons
    quantityContainer.classList.remove('quantity--unavailable');
    quantityInput.disabled = false;
    minusButton.disabled = value <= min; // Disable minus only at min
    plusButton.disabled = value >= max;  // Disable plus only at max
    
    // Make sure minus button is never disabled when at max quantity
    if (value >= max) {
      minusButton.disabled = false;
    }
  }
}

  // Initial state
  updateQuantityState();

  // Update on input change
  quantityInput.addEventListener('input', function() {
    let value = parseInt(quantityInput.value, 10);
    const min = parseInt(quantityInput.getAttribute('min')) || 1;
    const max = parseInt(quantityInput.getAttribute('max')) || Infinity;

    if (value < min) quantityInput.value = min;
    if (max !== Infinity && value > max) quantityInput.value = max;
    updateQuantityState();
  });

  // Plus button click
  plusButton.addEventListener('click', function() {
    const step = parseInt(quantityInput.getAttribute('step')) || 1;
    const max = parseInt(quantityInput.getAttribute('max')) || Infinity;
    let value = parseInt(quantityInput.value, 10);

    if (value < max) {
      quantityInput.value = value + step;
      updateQuantityState();
    }
  });

  // Minus button click
  minusButton.addEventListener('click', function() {
    const step = parseInt(quantityInput.getAttribute('step')) || 1;
    const min = parseInt(quantityInput.getAttribute('min')) || 1;
    let value = parseInt(quantityInput.value, 10);

    if (value > min) {
      quantityInput.value = value - step;
      updateQuantityState();
    }
  });
});


